/**
 * Microsoft Entra ID (Azure AD) MSAL Configuration
 * For HIPAA-compliant OAuth 2.0 authentication
 */

import { Configuration, PopupRequest, RedirectRequest } from '@azure/msal-browser'

export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID || '5f9d843a-9e35-4ad5-b4eb-1f99b823c3f8',
    authority: 'https://login.microsoftonline.com/organizations', // Multi-tenant: any Azure AD org
    redirectUri: window.location.origin,
    // The login page IS the request URL, so MSAL's default post-redirect hop
    // back to it just re-runs the SPA — a second perceived "refresh". Skip it.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage', // HIPAA: tokens never persisted to localStorage
    // Mirror only the transient OIDC handshake state (request state + nonce —
    // no tokens, no PHI, no user identity) to short-lived Secure cookies, so the
    // redirect response still resolves when a browser's cross-site storage
    // partitioning wipes sessionStorage between the hop to Entra and the return.
    // Tokens stay in sessionStorage; MSAL clears these cookies post-redirect.
    storeAuthStateInCookie: true,
    secureCookies: true,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.PROD ? 3 : 2, // Error in prod, Info in dev
      loggerCallback: (_level, message, containsPii) => {
        if (containsPii) return // Never log PII
        if (!import.meta.env.PROD) console.log(message)
      },
    },
  },
}

// `prompt: 'select_account'` forces the AS to display account selection (OIDC
// Core 1.0 §3.1.2.1). The practical consequence here is that MS skips its
// silent Browser-SSO probe (`sso_reload=true`), which on stale browser SSO
// state returns `#error=login_required` and surfaces as a perceived no-op
// refresh on the login page.
export const loginRequest: RedirectRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
  prompt: 'select_account',
}

export const apiRequest: PopupRequest = {
  // Use Microsoft Graph scopes to validate user token server-side
  scopes: ['User.Read', 'openid', 'profile', 'email'],
}

// ============================================================================
// Google OAuth Configuration
// ============================================================================

export const googleConfig = {
  clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '153410214288-poq46sd9781qukuhsgc9313u8lh57ti1.apps.googleusercontent.com',
}

// Authentication provider type
export type AuthProvider = 'microsoft' | 'google'

